import { floatingAskHeidiActiveAtom } from '@/atoms/floatingAskHeidiAtoms';
import { selectedNoteTextAtom } from '@/atoms/selectedNoteTextAtoms';
import { isEditorLive } from '@heidi/editor/core';
import { type Editor } from '@tiptap/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import {
  createSelectionContextHighlightPlugin,
  selectionContextHighlightKey,
  setSelectionContextHighlight,
} from './selection-context-highlight';

/** Unique id per hook instance so an editor only paints ranges it captured. */
let nextCaptureId = 0;

/**
 * Mirrors the editor's highlighted text into `selectedNoteTextAtom` so the
 * floating Ask Heidi bar can show the "Selected text" badge and scope sends
 * to it (selection → Ask Heidi).
 *
 * The atom is cleared when the selection collapses — unless the floating bar
 * is focused/typing, so clicking into the bar (which blurs the editor) never
 * drops the context the user just attached. While the context is held, the
 * captured range stays visibly highlighted in the note via a decoration
 * (browsers hide the native selection once the editor blurs).
 */
export const useSelectionCapture = (editor: Editor, enabled: boolean) => {
  const setSelectedNoteText = useSetAtom(selectedNoteTextAtom);
  const selectedNoteText = useAtomValue(selectedNoteTextAtom);
  const isFloatingBarActive = useAtomValue(floatingAskHeidiActiveAtom);

  const captureIdRef = useRef<number | null>(null);
  if (captureIdRef.current === null) {
    nextCaptureId += 1;
    captureIdRef.current = nextCaptureId;
  }

  // Read via ref inside the event handler to avoid re-subscribing (and to
  // always see the latest phase at event time).
  const isFloatingBarActiveRef = useRef(isFloatingBarActive);
  isFloatingBarActiveRef.current = isFloatingBarActive;

  // TipTap fires `transaction` before the companion `selectionUpdate`. A
  // collapsed selection after docChanged is usually a rebuild/edit artifact
  // (setContent, blur normalization) — not a user click-away. setContent can
  // also emit a follow-up selection-only transaction in the same turn, so we
  // suppress empty clears until the end of this task rather than reading the
  // latest transaction's docChanged bit alone.
  const suppressEmptyClearRef = useRef(false);

  // Decoration plugin that keeps the captured range visibly highlighted
  // while the editor is blurred (e.g. typing in the floating bar).
  useEffect(() => {
    if (!enabled || !isEditorLive(editor, 'useSelectionCapture.registerPlugin')) return;
    editor.registerPlugin(createSelectionContextHighlightPlugin());
    return () => {
      if (isEditorLive(editor, 'useSelectionCapture.unregisterPlugin')) {
        editor.unregisterPlugin(selectionContextHighlightKey);
      }
    };
  }, [editor, enabled]);

  // Paint the pinned highlight while this editor's capture is the active
  // context; clear it as soon as the context is dropped or replaced.
  const selectedNoteTextRef = useRef(selectedNoteText);
  selectedNoteTextRef.current = selectedNoteText;
  const prevSelectedNoteTextRef = useRef(selectedNoteText);
  useEffect(() => {
    if (!enabled || !isEditorLive(editor, 'useSelectionCapture.badgeDismiss')) return;
    const prev = prevSelectedNoteTextRef.current;
    prevSelectedNoteTextRef.current = selectedNoteText;
    if (
      selectedNoteText === null &&
      prev?.captureId === captureIdRef.current &&
      prev.from !== undefined &&
      prev.to !== undefined &&
      !editor.state.selection.empty
    ) {
      // Badge dismissed — collapse so selectionUpdate does not re-attach context
      // or grow the highlight on the next click.
      editor.commands.setTextSelection(prev.to);
    }
  }, [editor, enabled, selectedNoteText]);
  useEffect(() => {
    if (!enabled) return;
    const ownsContext =
      selectedNoteText?.captureId === captureIdRef.current &&
      selectedNoteText.from !== undefined &&
      selectedNoteText.to !== undefined;
    setSelectionContextHighlight(
      editor,
      ownsContext
        ? { from: selectedNoteText.from as number, to: selectedNoteText.to as number }
        : null
    );
  }, [editor, enabled, selectedNoteText]);

  // Resurrect the pinned highlight if a document rebuild drops it. Decoration
  // mapping survives ordinary edits, but full content replacement (blur-time
  // normalization, save echoes via setContent) discards mapped decorations
  // entirely — while the badge context lives on in the atom. Listen on
  // 'transaction' (not 'update'): save-echo appliers pass `emitUpdate: false`
  // explicitly, so 'update' would miss those rebuilds. On ordinary edits,
  // mirror the mapped decoration positions back into the atom so a later
  // rebuild repaints the correct range (not the frozen capture-time coords).
  useEffect(() => {
    if (!enabled) return;

    const handleTransaction = ({
      transaction,
    }: {
      transaction: { docChanged: boolean };
    }) => {
      if (transaction.docChanged) {
        suppressEmptyClearRef.current = true;
        queueMicrotask(() => {
          suppressEmptyClearRef.current = false;
        });
      }
      if (!transaction.docChanged) return;
      const value = selectedNoteTextRef.current;
      if (
        !value ||
        value.captureId !== captureIdRef.current ||
        value.from === undefined ||
        value.to === undefined
      ) {
        return;
      }
      const decorations = selectionContextHighlightKey.getState(editor.state);
      const mapped = decorations?.find()?.[0];
      if (mapped) {
        const { from, to } = mapped;
        if (from !== value.from || to !== value.to) {
          const text = editor.state.doc.textBetween(from, to, '\n').trim();
          // Never keep stale capture text against remapped coords — empty or
          // drifted live text means the selection is gone (e.g. deleted).
          if (!text) {
            setSelectedNoteText(null);
            setSelectionContextHighlight(editor, null);
            return;
          }
          setSelectedNoteText({
            ...value,
            from,
            to,
            text,
          });
        }
        return;
      }
      // Missing decoration is either a full rebuild (resurrect) or a delete of
      // the highlighted span (mapping dropped it). Only resurrect when the
      // frozen coords still hold the captured text — otherwise clear honestly.
      const docSize = editor.state.doc.content.size;
      const from = Math.max(0, Math.min(value.from, docSize));
      const to = Math.max(from, Math.min(value.to, docSize));
      const stillThere =
        from < to && editor.state.doc.textBetween(from, to, '\n').trim() === value.text;
      if (stillThere) {
        setSelectionContextHighlight(editor, { from: value.from, to: value.to });
      } else {
        setSelectedNoteText(null);
        setSelectionContextHighlight(editor, null);
      }
    };

    editor.on('transaction', handleTransaction);
    return () => {
      editor.off('transaction', handleTransaction);
    };
  }, [editor, enabled, setSelectedNoteText]);

  useEffect(() => {
    if (!enabled) return;

    const handleSelectionUpdate = () => {
      const { from, to, empty } = editor.state.selection;
      if (!empty) {
        const text = editor.state.doc.textBetween(from, to, '\n').trim();
        if (text) {
          setSelectedNoteText({
            text,
            from,
            to,
            captureId: captureIdRef.current ?? undefined,
          });
          return;
        }
      }
      // Clear only for a user-driven collapse on the owning editor. Cross-editor
      // selectionUpdates and rebuild/edit-driven collapses must not wipe context.
      if (
        !isFloatingBarActiveRef.current &&
        selectedNoteTextRef.current?.captureId === captureIdRef.current &&
        !suppressEmptyClearRef.current
      ) {
        setSelectedNoteText(null);
      }
    };

    editor.on('selectionUpdate', handleSelectionUpdate);
    return () => {
      editor.off('selectionUpdate', handleSelectionUpdate);
      // Editor going away (tab switch / unmount) invalidates the captured
      // text unless the user is mid-question in the floating bar — and only
      // if this instance owns the capture (another editor's teardown must
      // not wipe a context held by a different captureId).
      if (
        !isFloatingBarActiveRef.current &&
        selectedNoteTextRef.current?.captureId === captureIdRef.current
      ) {
        setSelectedNoteText(null);
      }
    };
  }, [editor, enabled, setSelectedNoteText]);
};
